import { post, get } from './api';
import { API_CONFIG } from 'src/config/api.config';

// Types for password reset requests/responses
export interface PasswordResetRequestDto {
  email: string;
  frontendBaseUrl?: string; // Add frontendBaseUrl for reset URLs
}

export interface PasswordResetResponseDto {
  success: boolean;
  message: string;
}

export interface PasswordResetChangeDto {
  token: string;
  password: string;
  confirmPassword: string;
}

// Add password reset endpoints to API_CONFIG
if (!API_CONFIG.endpoints.passwordReset) {
  API_CONFIG.endpoints.passwordReset = {
    request: 'v1/auth/password-reset/request',
    validate: 'v1/auth/password-reset/validate',
    change: 'v1/auth/password-reset/change'
  };
}

/**
 * Service for handling password reset functionality
 */
export const passwordResetService = {
  /**
   * Request a password reset for an email address
   *
   * @param email The email address to request a password reset for
   * @returns Promise with the request response
   */
  requestPasswordReset: async (
    email: string
  ): Promise<PasswordResetResponseDto> => {
    // Get the current origin (protocol + hostname + port)
    const frontendBaseUrl = window.location.origin;

    const requestData: Record<string, unknown> = {
      email,
      frontendBaseUrl // Include the frontend URL in the request
    };

    return post<PasswordResetResponseDto>(
      API_CONFIG.endpoints.passwordReset.request,
      requestData
    );
  },

  /**
   * Validate a password reset token
   *
   * @param token The token to validate
   * @returns Promise with the validation response
   */
  validateResetToken: async (
    token: string
  ): Promise<PasswordResetResponseDto> => {
    return get<PasswordResetResponseDto>(
      `${API_CONFIG.endpoints.passwordReset.validate}?token=${token}`
    );
  },

  /**
   * Reset a password using a valid token
   *
   * @param token The reset token
   * @param password The new password
   * @param confirmPassword Password confirmation
   * @returns Promise with the reset response
   */
  resetPassword: async (
    token: string,
    password: string,
    confirmPassword: string
  ): Promise<PasswordResetResponseDto> => {
    const resetData: Record<string, unknown> = {
      token,
      password,
      confirmPassword
    };
    return post<PasswordResetResponseDto>(
      API_CONFIG.endpoints.passwordReset.change,
      resetData
    );
  }
};

export default passwordResetService;
